import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { capitalCase } from 'text-case'
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField
} from '@mui/material'
import Content from 'components/content/content'
import Filter from 'components/filter/filter'
import Icon from 'components/icon'
import Loading from 'components/loading/loading'
import CONSTANT from 'modules/constant'
import {
  useDownloadRegionMutation,
  useGetRegionMutation,
  useActivateRegionMutation,
  useGetProvinceDropdownMutation,
  useGetCityDropdownMutation,
  useGetDistrictDropdownMutation,
  useGetSubDistrictDropdownMutation
} from 'store/region'
import type { PartialRegionProps } from 'modules/partial'
import Notification from 'components/notification'
import type { ErrorProps } from 'modules/types'
import GLOBAL from 'modules/global'
import MenuModalItem from 'components/menuItem/menuItem'
import GlobalStyle from 'modules/styles'
import Detail from './detail/detail'

const Data = () => {
  const [getProvinceDropdown, provinceDropdown] = useGetProvinceDropdownMutation()
  const [getCityDropdown, cityDropdown] = useGetCityDropdownMutation()
  const [getDistrictDropdown, districtDropdown] = useGetDistrictDropdownMutation()
  const [getSubDistrictDropdown, subDistrictDropdown] = useGetSubDistrictDropdownMutation()
  const [getRegion, region] = useGetRegionMutation()
  const [downloadRegion, download] = useDownloadRegionMutation()
  const [activateRegion, activate] = useActivateRegionMutation()

  const [dialog, setDialog] = useState<boolean>(false)
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [drawer, setDrawer] = useState<boolean>(false)
  const [menu, setMenu] = useState<null | number>(null)

  const [filter, setFilter] = useState({
    status: '',
    province: '',
    city: '',
    district: '',
    subdistrict: ''
  })

  const [payload, setPayload] = useState<PartialRegionProps>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    field: CONSTANT.DEFAULT_REGION_AUTH_FIELD.join(','),
    search: { value: '' },
    status: '',
    province: '',
    city: '',
    district: '',
    subdistrict: ''
  })

  const onFilter = (state: boolean) => setDialog(state)
  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: { value } })

  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const onAction = (event: React.MouseEvent<HTMLElement>, index: null | number) => {
    setAnchor(event.currentTarget)
    setMenu(index)
  }

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', String(0))
    formData.append('length', CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString())
    formData.append('field', payload.field)

    if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
    if (!isEmpty(payload.status) && payload.status) formData.append('status', payload.status)
    if (!isEmpty(payload.province)) formData.append('province', payload.province)
    if (!isEmpty(payload.city)) formData.append('city', payload.city)
    if (!isEmpty(payload.district) && payload.district)
      formData.append('district', payload.district)
    if (!isEmpty(payload.subdistrict) && payload.subdistrict)
      formData.append('subdistrict', payload.subdistrict)
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadRegion(formData)
  }

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', payload.field)

    if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
    if (!isEmpty(payload.status) && payload.status) formData.append('status', payload.status)
    if (!isEmpty(payload.province)) formData.append('province', payload.province)
    if (!isEmpty(payload.city)) formData.append('city', payload.city)
    if (!isEmpty(payload.district) && payload.district)
      formData.append('district', payload.district)
    if (!isEmpty(payload.subdistrict) && payload.subdistrict)
      formData.append('subdistrict', payload.subdistrict)

    getRegion(formData)
  }

  const onRemoveAnchor = async (callback: CallableFunction) => {
    setAnchor(null)
    callback(callback)
  }

  const tableHeadTitles = [
    'Province',
    'City',
    'District',
    'Subdistrict',
    'Postal Code',
    'Status',
    'Timezone'
  ]

  const openAnchor = Boolean(anchor)

  const totalPage = region.data && GLOBAL.tableTotalPage(region.data.recordsTotal)

  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload, activate])

  return (
    <>
      <Content
        title='Master Region Data List'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onFilter={() => onFilter(true)}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={region.data && region.data.recordsTotal}
        page={payload.start + 1}
        isLoading={region.isLoading}
        useDownloadDropdown
      >
        {region.isSuccess &&
          region.data &&
          region.data.data.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <IconButton
                  id={`button-${index}-${table.code}`}
                  aria-haspopup='true'
                  aria-controls={openAnchor ? `menu-${index}-${table.code}` : undefined}
                  aria-expanded={openAnchor ? 'true' : undefined}
                  onClick={(event) => onAction(event, index)}
                >
                  <Icon icon='MoreHoriz' />
                </IconButton>
                <Menu
                  id={`menu-${index}-${table.code}`}
                  aria-labelledby={`button-${index}-${table.code}`}
                  anchorEl={anchor}
                  open={openAnchor && menu == index}
                  onClose={() =>
                    onRemoveAnchor(() => {
                      setMenu(null)
                      setDrawer(false)
                    })
                  }
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  <MenuItem onClick={() => onRemoveAnchor(() => setDrawer(true))}>View</MenuItem>
                  <MenuModalItem
                    title={table.status == 'Enabled' ? 'Disable' : 'Enable'}
                    onClick={() =>
                      onRemoveAnchor(() =>
                        activateRegion({
                          id: table.subDistrictId,
                          isActive: table.status != 'Enabled'
                        })
                      )
                    }
                  >
                    {table.status == 'Enabled' ? 'Disable' : 'Enable'}
                  </MenuModalItem>
                </Menu>
                <Detail
                  id={table.subDistrictId}
                  open={drawer && menu == index}
                  onClose={() => setDrawer(false)}
                />
              </TableCell>
              <TableCell>{capitalCase(table.province || '-')}</TableCell>
              <TableCell>{capitalCase(table.city || '-')}</TableCell>
              <TableCell>{capitalCase(table.district || '-')}</TableCell>
              <TableCell>{capitalCase(table.subDistrict || '-')}</TableCell>
              <TableCell>{capitalCase(table.bpsCode || '-')}</TableCell>
              <TableCell>
                <Chip
                  label={table.status}
                  color={table.status == 'Enabled' ? 'success' : 'error'}
                />
              </TableCell>
              <TableCell>{table.timezone || '-'}</TableCell>
            </TableRow>
          ))}
      </Content>

      {region.isLoading && <Loading />}

      <Filter
        open={dialog}
        title='Filter'
        isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
        onClose={() => onFilter(false)}
        onCancel={() => onFilter(false)}
        onReset={() => {
          setFilter({
            status: '',
            province: '',
            city: '',
            district: '',
            subdistrict: ''
          })
          setPayload({
            ...payload,
            start: 0,
            status: '',
            province: '',
            city: '',
            district: '',
            subdistrict: ''
          })
          onFilter(false)
        }}
        onSubmit={() => {
          setPayload({ ...payload, start: 0, ...filter })
          onFilter(false)
        }}
      >
        <FormControl fullWidth>
          <InputLabel id='select-label'>Status</InputLabel>
          <Select
            labelId='select-label'
            id='id-select-label'
            value={filter.status}
            label='Status'
            onChange={(event) => setFilter({ ...filter, status: event.target.value })}
          >
            <MenuItem value='Enabled'>Enabled</MenuItem>
            <MenuItem value='Disabled'>Disabled</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          options={(provinceDropdown && provinceDropdown.data) || []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.id == value.id : false
          }
          onOpen={() => getProvinceDropdown()}
          onChange={(_, province) =>
            setFilter({
              ...filter,
              province: String(province && province.id),
              city: '',
              district: '',
              subdistrict: ''
            })
          }
          value={
            (provinceDropdown &&
              provinceDropdown.data &&
              provinceDropdown.data.find((e) => String(e.id) == filter.province)) ||
            null
          }
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name='province'
              label='Province'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {provinceDropdown.isLoading && <CircularProgress color='inherit' size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        <Autocomplete
          options={(cityDropdown && cityDropdown.data) || []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.id == value.id : false
          }
          onOpen={() => getCityDropdown(filter.province)}
          onChange={(_, city) =>
            setFilter({ ...filter, city: String(city && city.id), district: '', subdistrict: '' })
          }
          value={
            (cityDropdown &&
              cityDropdown.data &&
              cityDropdown.data.find((e) => String(e.id) == filter.city)) ||
            null
          }
          disabled={isEmpty(filter.province)}
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name='city'
              label='City'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {cityDropdown.isLoading && <CircularProgress color='inherit' size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        <Autocomplete
          options={(districtDropdown && districtDropdown.data) || []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.id == value.id : false
          }
          onOpen={() => getDistrictDropdown(filter.city)}
          onChange={(_, district) =>
            setFilter({ ...filter, district: String(district && district.id), subdistrict: '' })
          }
          value={
            (districtDropdown &&
              districtDropdown.data &&
              districtDropdown.data.find((e) => String(e.id) == filter.district)) ||
            null
          }
          disabled={isEmpty(filter.city)}
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name='district'
              label='District'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {districtDropdown.isLoading && <CircularProgress color='inherit' size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        <Autocomplete
          options={(subDistrictDropdown && subDistrictDropdown.data) || []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.id == value.id : false
          }
          onOpen={() => getSubDistrictDropdown(filter.district)}
          onChange={(_, subdistrict) =>
            setFilter({ ...filter, subdistrict: String(subdistrict && subdistrict.id) })
          }
          value={
            (subDistrictDropdown &&
              subDistrictDropdown.data &&
              subDistrictDropdown.data.find((e) => String(e.id) == filter.subdistrict)) ||
            null
          }
          disabled={isEmpty(filter.district)}
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name='subDistrict'
              label='Subdistrict'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {subDistrictDropdown.isLoading && (
                      <CircularProgress color='inherit' size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Filter>

      <Notification
        open={!region.isLoading && !region.isUninitialized && !region.isSuccess}
        onClose={() => (region.isError ? region.reset() : location.reload())}
        isError={Boolean(region.error) && region.isError}
        message={GLOBAL.returnExceptionMessage(region.isError, region.error as ErrorProps)}
      />

      <Notification
        open={!download.isLoading && !download.isUninitialized}
        onClose={() => download.reset()}
        isError={Boolean(download.error) && download.isError}
        message={GLOBAL.returnExceptionMessage(download.isError, download.error as ErrorProps)}
      />
    </>
  )
}

export default Data
